'use client'

import {
  IconChevronRight,
  IconFuelefficiencyFill,
  IconGasstationFill,
  IconStarFill,
} from '@macarongfactory/web-icons'
import { Text } from '@macarongfactory/web-ui'
import Link from 'next/link'

import {
  MODOOCAR_CODE,
  buildModoocarRankPath,
  useModoocarTracking,
} from '../_features/modoocar'

import type { TFuelType } from '../_features/modoocar'
import type { ReactNode } from 'react'

interface IRankChipsProps {
  satisfactionRank: number
  efficiencyRank: number
  fuelCostPercentile: number
  fuelType: TFuelType
  segment: string
  /** [정책]: A안(model_info)과 B안(model_compare)은 code만 다르고 type과 label이 같아서 두 안을 합쳐 비교할 수 있습니다. */
  trackCode?: string
}

const LEADING_ICON_SIZE_PX = 20
const CHEVRON_ICON_SIZE_PX = 14

const RankCard = ({
  icon,
  label,
  value,
  href,
  onClick,
}: {
  icon: ReactNode
  label: string
  value: ReactNode
  href: string
  onClick?: () => void
}) => (
  <Link
    href={href}
    onClick={onClick}
    className='flex flex-1 cursor-pointer flex-col justify-center gap-dimension-x3 rounded-dimension-x3 border border-stroke-neutral-strong bg-background-surface-default px-dimension-x3 py-dimension-x3-5 transition-colors hover:bg-background-surface-basement'
  >
    <div className='flex items-center justify-between gap-dimension-x1'>
      <div className='flex items-center gap-dimension-x0-5'>
        {icon}
        <Text variant='body-1-semibold' color='text-foreground-neutral-strong'>
          {label}
        </Text>
      </div>
      <span className='inline-flex text-foreground-neutral-assistive'>
        <IconChevronRight
          width={CHEVRON_ICON_SIZE_PX}
          height={CHEVRON_ICON_SIZE_PX}
        />
      </span>
    </div>
    {value}
  </Link>
)

const RankValue = ({ rank }: { rank: number }) => (
  <Text variant='heading-3-semibold' color='text-foreground-neutral-strong'>
    {rank}위
  </Text>
)

const PercentileValue = ({ percentile }: { percentile: number }) => (
  <div className='flex items-end gap-dimension-x1'>
    <span className='inline-flex items-center py-dimension-x0-5'>
      <Text variant='body-1-semibold' color='text-foreground-neutral-strong'>
        상위
      </Text>
    </span>
    <Text variant='heading-3-semibold' color='text-foreground-neutral-strong'>
      {percentile}%
    </Text>
  </div>
)

const RankChips = ({
  satisfactionRank,
  efficiencyRank,
  fuelCostPercentile,
  fuelType,
  segment,
  trackCode = MODOOCAR_CODE.MODEL_INFO,
}: IRankChipsProps) => {
  const { itemClick } = useModoocarTracking()
  const trackChip = (label: string) => itemClick(trackCode, 'stat_chip', label)

  const rankHref = (tab: 'score' | 'efficiency' | 'fuelCost') =>
    buildModoocarRankPath({ tab, segment, fuelType })

  const isElectric = fuelType === '전기'
  const efficiencyLabel = isElectric ? '실전비' : '실연비'
  const fuelCostLabel = isElectric ? '전기비' : '유류비'

  const chips = [
    satisfactionRank > 0 && (
      <RankCard
        key='satisfaction'
        icon={
          <span className='inline-flex text-light-amber-400'>
            <IconStarFill
              width={LEADING_ICON_SIZE_PX}
              height={LEADING_ICON_SIZE_PX}
            />
          </span>
        }
        label='만족도'
        value={<RankValue rank={satisfactionRank} />}
        href={rankHref('score')}
        onClick={() => trackChip('평점')}
      />
    ),
    efficiencyRank > 0 && (
      <RankCard
        key='efficiency'
        icon={
          <span className='inline-flex text-foreground-success-strong'>
            <IconFuelefficiencyFill
              width={LEADING_ICON_SIZE_PX}
              height={LEADING_ICON_SIZE_PX}
            />
          </span>
        }
        label={efficiencyLabel}
        value={<RankValue rank={efficiencyRank} />}
        href={rankHref('efficiency')}
        onClick={() => trackChip('연비')}
      />
    ),
    fuelCostPercentile > 0 && (
      <RankCard
        key='fuelCost'
        icon={
          <span className='inline-flex text-foreground-brand-strong'>
            <IconGasstationFill
              width={LEADING_ICON_SIZE_PX}
              height={LEADING_ICON_SIZE_PX}
            />
          </span>
        }
        label={fuelCostLabel}
        value={<PercentileValue percentile={fuelCostPercentile} />}
        href={rankHref('fuelCost')}
        onClick={() => trackChip('유류비')}
      />
    ),
  ].filter(Boolean)

  if (!segment || chips.length === 0) return null

  return <div className='flex gap-dimension-x1-5'>{chips}</div>
}

export default RankChips
